import { useParams, Link } from "react-router-dom";
import { getArticleById, popularArticles } from "@/mocks/data";
import SEO from "@/components/base/SEO";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || "");

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-foreground-950 mb-2">文章未找到</h2>
          <Link to="/" className="text-primary-600 hover:underline text-sm cursor-pointer">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedArticles = popularArticles
    .filter((a) => a.id !== article.id)
    .slice(0, 3);

  const seoData = {
    title: article.title,
    description: article.excerpt,
    keywords: `${article.tags.join(",")},福利导航,${article.categoryName}福利`,
    canonicalUrl: `https://jobbang.cn/article/${article.id}`,
    ogType: "article",
    schema: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      image: article.image,
      datePublished: article.date,
      author: {
        "@type": "Organization",
        name: "福利导航",
      },
      publisher: {
        "@type": "Organization",
        name: "福利导航",
        url: "https://jobbang.cn",
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `https://jobbang.cn/article/${article.id}`,
      },
    },
  };

  return (
    <>
      <SEO data={seoData} />
      <main className="min-h-screen bg-background-50 pt-24 pb-12 md:pb-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: "首页", href: "/" },
              { label: article.categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <header className="mt-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Link
                to={`/category/${article.category}`}
                className="px-2.5 py-1 rounded-md text-xs font-medium bg-primary-100/70 text-primary-700 hover:bg-primary-200/70 transition-colors cursor-pointer"
              >
                {article.categoryName}
              </Link>
              {article.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="px-2.5 py-1 rounded-md text-xs bg-secondary-100 text-secondary-700"
                >
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-xl md:text-3xl font-bold text-foreground-950 font-heading leading-snug mb-4">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                阅读约 {article.readTime}
              </span>
            </div>
          </header>

          <div className="mb-10 rounded-lg overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-auto object-cover max-h-[400px]"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
              prose-h2:text-lg md:prose-h2:text-xl prose-h2:font-semibold prose-h2:text-foreground-950 prose-h2:mt-8 prose-h2:mb-4
              prose-p:mb-4 prose-p:leading-relaxed
              prose-strong:text-foreground-900"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-10 pt-6 border-t border-background-200/70">
            <h3 className="text-lg font-semibold text-foreground-950 mb-4">相关文章推荐</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/article/${related.id}`}
                  className="group bg-background-100 rounded-lg p-4 hover:bg-background-200/70 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-8 h-8 flex-shrink-0 rounded-md overflow-hidden">
                      <img
                        src={related.image}
                        alt={related.title}
                        className="w-full h-full object-cover"
                      />
                    </span>
                    <h4 className="text-sm font-medium text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {related.title}
                    </h4>
                  </div>
                  <p className="text-xs text-foreground-500 line-clamp-2">{related.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </article>
      </main>
    </>
  );
}